/**
 * The Notion import's DTOs (CS r4a deliverable 102; DECISIONS 176 to 178): a run with its upload,
 * plan, mapping and counts; a page of the match review with its tier, candidates, re-import
 * action and choice; the dry-run report. The routes are `routes/import.ts` (owner 2.B-1); the
 * values are `rows-enums.ts`'s import section. Types and fields are made through 1.B-1's routes
 * before a mapping names them. CS r4c (deliverables 128 and 129) adds the single pages' mapping,
 * the run's value failures and the dry run's two single-page answers, each optional.
 */
import { CANON_STATUSES, IMPORT_CHANGED_IN_BOTH_DEFAULTS, IMPORT_CHOICE_BY, IMPORT_CHOICES, IMPORT_CHOICES_BY_TIER, IMPORT_DATE_AS, IMPORT_FAILURE_CODES, IMPORT_MATCH_TIERS, IMPORT_OUTCOMES, IMPORT_PAIR_DEFAULTS, IMPORT_PROPERTY_BLOCK_CHOICES, IMPORT_REIMPORT_ACTIONS, IMPORT_RUN_STATUSES, } from '../domain/rows-enums.js';
import { ImportExportKind, ImportPlanStatsV1, ImportWarningCode, ImportWarningV1, NotionId, NotionPropertyName, Sha256Hex, } from '../import/plan.js';
import { z } from '../zod.js';
import { IsoTimestamp, RowVersion, Uuid } from './common.js';
import { DocBodyV1, TypeKey } from './documents.js';
import { Cursor } from './list.js';
import { queryBool, queryInt } from './query.js';
import { FieldKey } from './types.js';
/** One upload part: 8 MiB, the last one the remainder (DECISIONS 176 item 2). */
export const IMPORT_PART_BYTES = 8 * 1024 * 1024;
/** The largest export accepted: 2 GiB until the GM's export is sized (open point 14). */
export const IMPORT_MAX_BYTES = 2 * 1024 * 1024 * 1024;
/** The most parts an upload has: `IMPORT_MAX_BYTES / IMPORT_PART_BYTES`. */
export const IMPORT_MAX_PARTS = IMPORT_MAX_BYTES / IMPORT_PART_BYTES;
export const ImportRunStatus = z.enum(IMPORT_RUN_STATUSES);
export const ImportFailureCode = z.enum(IMPORT_FAILURE_CODES);
export const ImportMatchTier = z.enum(IMPORT_MATCH_TIERS);
export const ImportChoice = z.enum(IMPORT_CHOICES);
export const ImportChoiceBy = z.enum(IMPORT_CHOICE_BY);
export const ImportReimportAction = z.enum(IMPORT_REIMPORT_ACTIONS);
export const ImportOutcome = z.enum(IMPORT_OUTCOMES);
export const ImportDateAs = z.enum(IMPORT_DATE_AS);
export const ImportPropertyBlock = z.enum(IMPORT_PROPERTY_BLOCK_CHOICES);
/** True when `choice` is one the tier offers (`IMPORT_CHOICES_BY_TIER`). */
export function isChoiceOfTier(tier, choice) {
    return IMPORT_CHOICES_BY_TIER[tier].includes(choice);
}
const Count = z.number().int().nonnegative();
/** The canon statuses a Notion status option may map to: any but `reference`. */
const MappedCanonStatus = z.enum(CANON_STATUSES).exclude(['reference']);
/**
 * The single pages of a `markdown_pages` export (CS r4c deliverable 128): the type every page is
 * created as (in place of 2.B-1's free-page fallback for these runs), and each property of their
 * property blocks to a field or to null, once by `notionName`, with the database list's bounds.
 * `stubTypeKey` is the canon type a relation stub of the property is created as (a note when
 * absent; amendments 2.2 §5, "Stubs"). No statuses: the page grammar has no status kind.
 */
export const ImportMappingSinglePagesV1 = z.strictObject({
    typeKey: TypeKey,
    properties: z
        .array(z.strictObject({
        notionName: NotionPropertyName,
        fieldKey: FieldKey.nullable(),
        dateAs: ImportDateAs.optional(),
        stubTypeKey: TypeKey.optional(),
    }))
        .max(10_000),
});
/**
 * The GM's mapping (DECISIONS 176 items 4 and 5, 178): each database to a type or to null ("Don't
 * import"), each property to a field or to null, each status option to a canon status or to null,
 * the answer for single pages' property blocks, the single pages' type and properties (CS r4c),
 * and the bulk defaults. A database without an entry is undecided.
 */
export const ImportMappingV1 = z
    .strictObject({
    databases: z
        .array(z.strictObject({ databaseId: NotionId, typeKey: TypeKey.nullable() }))
        .max(500),
    properties: z
        .array(z.strictObject({
        databaseId: NotionId,
        notionName: NotionPropertyName,
        fieldKey: FieldKey.nullable(),
        dateAs: ImportDateAs.optional(),
    }))
        .max(10_000),
    statuses: z
        .array(z.strictObject({
        databaseId: NotionId,
        notionName: NotionPropertyName,
        option: z.string().max(500),
        canonStatus: MappedCanonStatus.nullable(),
    }))
        .max(10_000),
    /** Only for export kind `markdown_pages` (amendments 2.2 §5 "Property grammar" item 3). */
    propertyBlock: ImportPropertyBlock.optional(),
    /**
     * Only for export kind `markdown_pages` (CS r4c); its properties only under an accepted
     * property block. Kind suitability, live types and the reference refusal are 2.B-1's checks.
     */
    singlePages: ImportMappingSinglePagesV1.optional(),
    defaults: z.strictObject({
        pair: z.enum(IMPORT_PAIR_DEFAULTS),
        changedInBoth: z.enum(IMPORT_CHANGED_IN_BOTH_DEFAULTS),
    }),
})
    .superRefine((mapping, ctx) => {
    const once = (keys, list, message) => {
        const seen = new Set();
        keys.forEach((key, i) => {
            if (seen.has(key))
                ctx.addIssue({ code: 'custom', message, path: [list, i] });
            seen.add(key);
        });
    };
    once(mapping.databases.map((d) => d.databaseId), 'databases', 'a database is mapped twice');
    once(mapping.properties.map((p) => JSON.stringify([p.databaseId, p.notionName])), 'properties', 'a property is mapped twice');
    once(mapping.statuses.map((s) => JSON.stringify([s.databaseId, s.notionName, s.option])), 'statuses', 'a status option is mapped twice');
    const single = mapping.singlePages;
    if (single === undefined)
        return;
    const seen = new Set();
    single.properties.forEach((property, i) => {
        const at = ['singlePages', 'properties', i];
        if (seen.has(property.notionName)) {
            ctx.addIssue({
                code: 'custom',
                message: 'a single-page property is mapped twice',
                path: at,
            });
        }
        seen.add(property.notionName);
        if (property.stubTypeKey !== undefined && property.fieldKey === null) {
            ctx.addIssue({
                code: 'custom',
                message: 'a stub type needs the property mapped to a field',
                path: [...at, 'stubTypeKey'],
            });
        }
    });
    if (single.properties.length > 0 && mapping.propertyBlock !== 'accept') {
        ctx.addIssue({
            code: 'custom',
            message: 'single-page properties are mapped only when the property block is accepted',
            path: ['singlePages', 'properties'],
        });
    }
});
/** Why a run failed: an archive refusal (with the entry's index) or a failed plan, apply or undo. */
export const ImportFailureV1 = z.strictObject({
    code: ImportFailureCode,
    entryIndex: Count.nullable(),
});
/**
 * The values of one property an apply could not write into its field (CS r4c deliverable 129): the
 * database (null for single pages), the property, the field and how many values failed.
 */
export const ImportValueFailureV1 = z.strictObject({
    databaseId: NotionId.nullable(),
    notionName: NotionPropertyName,
    fieldKey: FieldKey,
    failures: z.number().int().min(1),
});
/** A run: the upload, the plan's head, the mapping, the outcome counts and the lifecycle. */
export const ImportRunV1 = z
    .strictObject({
    id: Uuid,
    rv: RowVersion,
    status: ImportRunStatus,
    fileName: z.string().min(1).max(255),
    bytes: z.number().int().min(1).max(IMPORT_MAX_BYTES),
    partsTotal: z.number().int().min(1).max(IMPORT_MAX_PARTS),
    partsReceived: z.number().int().min(0).max(IMPORT_MAX_PARTS),
    /** Null until the plan job has read the export. */
    exportKind: ImportExportKind.nullable(),
    planHash: Sha256Hex.nullable(),
    stats: ImportPlanStatsV1.nullable(),
    mapping: ImportMappingV1.nullable(),
    /** Pages by outcome, once an apply or an undo ran. */
    counts: z.partialRecord(ImportOutcome, Count).nullable(),
    /** Pages Undo import left alone because they were edited since. */
    undoRefused: Count,
    /**
     * Per property, the values the apply could not write (CS r4c): absent until an apply records
     * them; an undo leaves them (`import_run.value_failures`).
     */
    valueFailures: z.array(ImportValueFailureV1).max(10_000).optional(),
    failure: ImportFailureV1.nullable(),
    /** When Cancel reached an `applying` run; null otherwise (DECISIONS 178). */
    cancelRequestedAt: IsoTimestamp.nullable(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    appliedAt: IsoTimestamp.nullable(),
    undoneAt: IsoTimestamp.nullable(),
})
    .superRefine((run, ctx) => {
    if (run.partsTotal !== Math.ceil(run.bytes / IMPORT_PART_BYTES)) {
        ctx.addIssue({
            code: 'custom',
            message: 'partsTotal is the bytes in parts of IMPORT_PART_BYTES',
            path: ['partsTotal'],
        });
    }
    if (run.partsReceived > run.partsTotal) {
        ctx.addIssue({
            code: 'custom',
            message: 'more parts received than the upload has',
            path: ['partsReceived'],
        });
    }
    if (run.status === 'failed' && run.failure === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'a failed run names its failure',
            path: ['failure'],
        });
    }
    if (run.mapping?.propertyBlock !== undefined && run.exportKind !== 'markdown_pages') {
        ctx.addIssue({
            code: 'custom',
            message: 'propertyBlock is only for single Markdown pages',
            path: ['mapping', 'propertyBlock'],
        });
    }
    if (run.mapping?.singlePages !== undefined && run.exportKind !== 'markdown_pages') {
        ctx.addIssue({
            code: 'custom',
            message: 'singlePages is only for single Markdown pages',
            path: ['mapping', 'singlePages'],
        });
    }
});
// ---- Requests ----
/** `import.runs.create`: the file's name and size; the parts follow. */
export const ImportRunCreateV1 = z.strictObject({
    fileName: z.string().min(1).max(255),
    bytes: z.number().int().min(1).max(IMPORT_MAX_BYTES),
});
/** `import.runs.uploadPart`'s path: the run and the part's index, from 0. */
export const ImportPartParams = z.strictObject({
    id: Uuid,
    n: queryInt(0, IMPORT_MAX_PARTS - 1),
});
/** The form fields of an upload part beside the file: none. */
export const ImportPartFieldsV1 = z.strictObject({});
/** The media types a part may carry: a slice of the zip, as a browser labels it. */
export const IMPORT_PART_MIME_TYPES = ['application/octet-stream', 'application/zip'];
export const ImportPartResultV1 = z.strictObject({
    partsReceived: z.number().int().min(0).max(IMPORT_MAX_PARTS),
    bytesReceived: z.number().int().min(0).max(IMPORT_MAX_BYTES),
});
/** `import.runs.complete`: the browser's SHA-256 of the whole file (`@noble/hashes`). */
export const ImportRunCompleteV1 = z.strictObject({ sha256: Sha256Hex });
export const ImportMappingPutV1 = z.strictObject({ baseRv: RowVersion, mapping: ImportMappingV1 });
export const ImportApplyV1 = z.strictObject({ baseRv: RowVersion });
/** `import.pages.get`'s path: the run and the page's Notion id. */
export const ImportPageParams = z.strictObject({ id: Uuid, notionId: NotionId });
const filterShape = {
    tier: ImportMatchTier.optional(),
    choice: ImportChoice.optional(),
    reimport: ImportReimportAction.optional(),
    typeKey: TypeKey.optional(),
};
/** `import.pages.list`'s query: filters and the page (`limit` 1 to 200, default 50). */
export const ImportPagesQueryV1 = z.strictObject({
    ...filterShape,
    skippedBefore: queryBool().optional(),
    cursor: Cursor.optional(),
    limit: queryInt(1, 200).default(50),
});
/** The pages a bulk choice applies to, by the list's filters. */
export const ImportPagesFilterV1 = z.strictObject({
    ...filterShape,
    skippedBefore: z.boolean().optional(),
});
/**
 * `import.pages.resolve`: one choice for 1 to 500 pages named by id, or for every page the filter
 * selects ("Apply to selected"), with the target document for a Fill or a Pair of several
 * candidates. A choice outside a page's tier, or a choice for a `gone` page, answers 422.
 */
export const ImportPagesResolveV1 = z
    .strictObject({
    notionIds: z.array(NotionId).min(1).max(500).optional(),
    filter: ImportPagesFilterV1.optional(),
    choice: ImportChoice,
    targetDocId: Uuid.optional(),
})
    .refine((r) => (r.notionIds === undefined) !== (r.filter === undefined), {
    message: 'name the pages by notionIds or by filter, not both',
    path: ['notionIds'],
});
export const ImportPagesResolveResultV1 = z.strictObject({ updated: Count });
// ---- Pages and the dry run ----
/** A document the page may match: its type and whether its alias, not its title, matched. */
export const ImportCandidateV1 = z.strictObject({
    documentId: Uuid,
    typeKey: TypeKey,
    isAlias: z.boolean(),
});
const pageShape = {
    notionId: NotionId,
    title: z.string().max(2000),
    databaseId: NotionId.nullable(),
    depth: z.number().int().min(0).max(32),
    /** The type its database is mapped to, or null (a free page, or a database not mapped yet). */
    typeKey: TypeKey.nullable(),
    tier: ImportMatchTier,
    candidates: z.array(ImportCandidateV1).max(50),
    /** Another page of the export with the same type and title: no default (2.A deliverable 30). */
    inExportDuplicate: z.boolean(),
    /** The chosen target: the mapped, filled or paired document; null to create or skip. */
    documentId: Uuid.nullable(),
    /** For a `mapped` page, 2.A's `diffReimport` action; null otherwise. */
    reimport: ImportReimportAction.nullable(),
    /** Body similarity to a single `title` candidate, 0 to 1. */
    similarity: z.number().min(0).max(1).nullable(),
    /** An SRD entry of the same title: a note, never a target. */
    srdDocumentId: Uuid.nullable(),
    choice: ImportChoice.nullable(),
    choiceBy: ImportChoiceBy.nullable(),
    /** An earlier run skipped this page: listed under Skipped, default `skip` (DECISIONS 178). */
    skippedBefore: z.boolean(),
    outcome: ImportOutcome.nullable(),
    warnings: z.array(ImportWarningV1),
};
/** A choice within its tier; none for a page gone from Notion; a re-import action only if mapped. */
function pageRule(page, ctx) {
    if (page.choice !== null && !isChoiceOfTier(page.tier, page.choice)) {
        ctx.addIssue({
            code: 'custom',
            message: `the choice is not one tier ${page.tier} offers`,
            path: ['choice'],
        });
    }
    if ((page.reimport === 'gone' || page.reimport === 'stub_unresolved') && page.choice !== null) {
        ctx.addIssue({
            code: 'custom',
            message: 'a page gone from the export takes no choice',
            path: ['choice'],
        });
    }
    if (page.reimport !== null && page.tier !== 'mapped') {
        ctx.addIssue({
            code: 'custom',
            message: 'only a mapped page has a re-import action',
            path: ['reimport'],
        });
    }
    if ((page.choice === null) !== (page.choiceBy === null)) {
        ctx.addIssue({
            code: 'custom',
            message: 'choiceBy is set exactly when a choice is',
            path: ['choiceBy'],
        });
    }
}
/** One page of a run's match review (`import.pages.list`). */
export const ImportPageV1 = z.strictObject(pageShape).superRefine(pageRule);
/** A page with its body as 2.A's `readPage` gives it, for Compare (`import.pages.get`). */
export const ImportPageDetailV1 = z
    .strictObject({ ...pageShape, body: DocBodyV1 })
    .superRefine(pageRule);
/** What an apply would do, written by nothing (2.B-1 deliverable 12). */
export const ImportDryRunV1 = z.strictObject({
    /** Pages per mapped type (null: free pages) and choice. */
    byTypeAndChoice: z.array(z.strictObject({ typeKey: TypeKey.nullable(), choice: ImportChoice, pages: Count })),
    unchanged: Count,
    changedInBoth: Count,
    gone: Count,
    /** The relation stubs an apply would create. */
    unresolvedRelations: Count,
    /** Dates that do not parse, per property. */
    dateFailures: z.array(z.strictObject({ databaseId: NotionId, notionName: NotionPropertyName, failures: Count })),
    /** The single pages' dates that do not parse, per property (CS r4c; `dateFailures`' twin). */
    singlePageDateFailures: z
        .array(z.strictObject({ notionName: NotionPropertyName, failures: Count }))
        .optional(),
    /** Pages past the 20,000-word nudge (PLAN §2, Long documents): listed, never blocked. */
    longPages: z.array(z.strictObject({ notionId: NotionId, wordCount: Count })),
    images: z.strictObject({ found: Count, missing: Count }),
    /** Pages titled as an SRD entry: a note each. */
    srdNotes: Count,
    warnings: z.partialRecord(ImportWarningCode, Count),
    /**
     * Pages without a choice and databases without a mapping entry (DECISIONS 178); `singlePages`
     * (CS r4c): the plan has single pages and the mapping none.
     */
    undecided: z.strictObject({
        pages: Count,
        databases: Count,
        singlePages: z.boolean().optional(),
    }),
});
//# sourceMappingURL=import.js.map